<template>
  <div>
    <div v-for="(answer, i) in currentAnswers" :key="i" class="answer-block">
      <VTextField
        v-model="answer.answer"
        required
        outlined
        dense
        type="text"
        class="answer-textfield"
        :hint="`${$t('charactersLimit')} - 255`"
        :counter="255"
        :error-messages="answer.errors"
        :placeholder="answer.isCorrect ? $t('enterTheCorrectAnswer') : $t('enterTheAnswer')"
        @keydown.enter.prevent
        @input="saveAnswer"
      />

      <VCheckbox
        v-model="answer.isCorrect"
        class="answer-checkbox"
        :label="$t('correctAnswer')"
        @change="saveAnswer()"
      />

      <div v-if="currentAnswers.length > 1" class="answer-controls">
        <div @click="deleteAnswer(answer)" class="answer-trash">
          <img :src="require('@/assets/img/test/trash.svg')" alt="trash">
        </div>
      </div>
    </div>

    <div v-if="showAnotherAnswerRequired" class="error-message">
      {{ $t('addMoreAnswersError') }}
    </div>

    <div v-if="hasNoCorrectAnswers" class="error-message">
      {{ $t('addOneCorrectAnswerError') }}
    </div>

    <div class="add-answer-block">
      <span class="add-answer" @click="addAnotherAnswer">
        {{ $t('addAnAnswerOption') }}
      </span>
    </div>
  </div>
</template>

<script>
import {
  computed, onMounted, ref, toRefs, watch,
} from '@vue/composition-api';
import { useI18n } from 'vue-i18n-composable';
import { testQuestionTypes } from '@/enums';

export default {
  name: 'TestQuestionSelectType',
  props: {
    answers: {
      type: Array,
      required: true,
    },
    showValidationMessagesGlobal: {
      type: Boolean,
      required: true,
    },
  },
  setup(props, { emit }) {
    const { answers, showValidationMessagesGlobal } = toRefs(props);
    const { t } = useI18n();

    const currentAnswers = ref(answers.value);
    const showValidationMessages = ref(false);
    const answerAdded = ref(false);

    function getAnswerErrors(a) {
      const errors = [];

      if (a.emptyAnswerByUser || (showValidationMessages.value && a.isEmptyAnswer)) {
        errors.push(t('requiredField'));
      }

      if (a.isDuplicatedAnswer) {
        errors.push(t('duplicateAnswersError'));
      }

      if (a.isAsnwserTooLong) {
        errors.push(`${t('charactersLimit')} - ${255}`);
      }

      return errors;
    }

    const anotherAnswerRequired = computed(
      () => currentAnswers.value.length <= 1,
    );

    const showAnotherAnswerRequired = computed(
      () => anotherAnswerRequired.value && (answerAdded.value || showValidationMessages.value),
    );

    const hasNoCorrectAnswers = computed(
      () => currentAnswers.value.filter((ca) => ca.isCorrect).length === 0,
    );

    const processedAnswers = computed(() => {
      return currentAnswers.value.map((answer) => {
        answer.isEmptyAnswer = !answer.answer || !answer.answer.trim();
        answer.isAsnwserTooLong = answer?.answer?.length > 255;

        const sameAnswersCount = currentAnswers.value.filter((a) => a.answer === answer.answer && answer.answer).length;
        answer.isDuplicatedAnswer = sameAnswersCount > 1;
        answer.emptyAnswerByUser = answer.answer === '';

        answer.anotherAnswerRequired = anotherAnswerRequired.value;
        answer.hasNoCorrectAnswers = hasNoCorrectAnswers.value;

        answer.isValid = !(
          answer.isDuplicatedAnswer
          || answer.isEmptyAnswer
          || answer.isAsnwserTooLong
          || answer.hasNoCorrectAnswers
          || answer.anotherAnswerRequired
        );

        answer.errors = getAnswerErrors(answer);

        return answer;
      });
    });

    const questionType = computed(() => {
      return currentAnswers.value.filter((ca) => ca.isCorrect).length <= 1
        ? testQuestionTypes.SELECT
        : testQuestionTypes.MULTISELECT;
    });

    function saveAnswer(initial = false) {
      currentAnswers.value = processedAnswers.value;

      if (initial === true) {
        emit('initialChangeQuestionType', questionType.value);
        emit('initialChangeAnswers', processedAnswers.value);
        return;
      }

      emit('changeQuestionType', questionType.value);
      emit('changeAnswers', processedAnswers.value);
    }

    function deleteAnswer(a) {
      if (currentAnswers.value.length <= 1) {
        return;
      }

      currentAnswers.value = currentAnswers.value.filter((answer) => answer.id !== a.id);
      saveAnswer();
    }

    function addAnotherAnswer() {
      currentAnswers.value.push(
        {
          id: -1 * (currentAnswers.value.length + 1), isCorrect: false, answer: null,
        },
      );
      answerAdded.value = true;
      saveAnswer();
    }

    watch(showValidationMessagesGlobal, (flag) => {
      if (!flag) {
        return;
      }

      showValidationMessages.value = true;
      currentAnswers.value = processedAnswers.value;
    });

    onMounted(() => {
      saveAnswer(true);
    });

    return {
      deleteAnswer,
      addAnotherAnswer,
      currentAnswers,
      saveAnswer,
      showValidationMessages,
      hasNoCorrectAnswers,
      anotherAnswerRequired,
      showAnotherAnswerRequired,
    };
  },
};
</script>

<style lang="scss" scoped>
.add-answer-block {
  @media screen and (max-width: 599px) {
    margin-top: 20px;
  }
}
.add-answer {
  border-bottom: 1px dashed #0A82D9;
  color: #0A82D9;
  font-size: 14px;
  cursor: pointer;
}
.answer-block {
  display: flex;
  width: 75%;
  @media screen and (max-width: 768px) {
    flex-wrap: wrap;
  }
  @media screen and (max-width: 900px) {
    width: 100%;
  }
}
.answer-textfield {
  max-width: 350px;
  @media screen and (max-width: 768px) {
    width: 100%;
    max-width: 90%;
  }
}

.answer-controls {
  margin: 10px 0 0 8px;
  order: 2;
  @media screen and (max-width: 768px) {
    order: 1;
  }
}

.answer-trash {
  margin-left: 20px;
  cursor: pointer;
  @media screen and (max-width: 768px) {
    margin-left: 10px;
  }
}
.answer-checkbox {
  order: 1;
  margin: 4px 0 0 8px;
  @media screen and (max-width: 768px) {
    order: 2;
    margin: 0 0 15px 0;
  }
}

.error-message {
  color: #ff5252;
  font-size: 12px;
}

.v-input--checkbox::v-deep {
  .v-input__control {
    .v-input__slot {
      margin-bottom: 0;
    }
  }
}
</style>

<i18n locale="en" lang="yaml">
  charactersLimit: The maximum number of characters is
  enterTheCorrectAnswer: 'Enter the correct answer*'
  enterTheAnswer: 'Enter the answer*'
  correctAnswer: Correct answer
  requiredField: This field is required
  addAnAnswerOption: Add an answer option
  duplicateAnswersError: This type of question cannot have several identical answers
  addMoreAnswersError: This type of question cannot have one or fewer possible answers
  addOneCorrectAnswerError: This type of question cannot have less than one correct answer
</i18n>

<i18n locale="ru" lang="yaml">
  charactersLimit: Максимальное количество символов
  enterTheCorrectAnswer: 'Введите правильный ответ*'
  enterTheAnswer: 'Введите ответ*'
  correctAnswer: Правильный ответ
  requiredField: Обязательное поле для заполнения
  addAnAnswerOption: Добавить вариант ответа
  duplicateAnswersError: У данного типа вопроса не может быть несколько одинаковых ответов
  addMoreAnswersError: У данного типа вопроса не может быть одного или менее вариантов ответов
  addOneCorrectAnswerError: У данного типа вопроса не может быть менее одного правильного варианта ответа
</i18n>
